/**
 * Hora del consultorio en la UI (alineado con backend: CALENDAR_TIMEZONE, p. ej. America/Mexico_City).
 *
 * Contrato API relevante:
 * - GET /admin/calendar-events: `start_at` / `end_at` de cada evento en RFC 3339 **UTC** (fin de semana ISO).
 * - Citas en Mongo: `date` = "YYYY-MM-DD" en calendario del consultorio; `time` = "HH:MM" local CDMX.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { addDays, addMonths } from 'date-fns';
import { parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

export const CLINIC_TIMEZONE = 'America/Mexico_City';

const defaultLocale = es;

export function formatNowMexicoCityLabel() {
  const d = new Date();
  const datePart = formatInTimeZone(d, CLINIC_TIMEZONE, 'EEE, d MMM yyyy', { locale: defaultLocale });
  const timePart = formatInTimeZone(d, CLINIC_TIMEZONE, 'HH:mm:ss', { locale: defaultLocale });
  return {
    datePart,
    timePart,
    fullLabel: `${datePart} · ${timePart} (CDMX)`,
  };
}

/** ISO instantáneo (UTC con Z u offset) → texto en hora de la clínica. */
export function formatUtcIsoInClinic(isoString, fmt, options = {}) {
  if (!isoString) return '';
  try {
    const d = parseISO(String(isoString));
    if (Number.isNaN(d.getTime())) return '';
    return formatInTimeZone(d, CLINIC_TIMEZONE, fmt, { locale: defaultLocale, ...options });
  } catch {
    return '';
  }
}

/** Clave yyyy-MM-dd en zona clínica (para agrupar eventos en celdas del calendario). */
export function calendarDayKeyInClinic(instant) {
  try {
    const d = instant instanceof Date ? instant : parseISO(String(instant));
    if (Number.isNaN(d.getTime())) return '';
    return formatInTimeZone(d, CLINIC_TIMEZONE, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

/**
 * Campo `date` solo fecha (YYYY-MM-DD) guardado como día de consultorio en CDMX.
 */
export function formatAppointmentDateOnlyInClinic(yyyyMmDd, fmt, options = {}) {
  const raw = String(yyyyMmDd || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  try {
    const d = fromZonedTime(`${raw}T00:00:00`, CLINIC_TIMEZONE);
    return formatInTimeZone(d, CLINIC_TIMEZONE, fmt, { locale: defaultLocale, ...options });
  } catch {
    return '';
  }
}

/** yyyy-MM-dd del «hoy» en la zona del consultorio. */
export function todayYmdInClinic() {
  return formatInTimeZone(new Date(), CLINIC_TIMEZONE, 'yyyy-MM-dd');
}

export function addCalendarDaysYmd(ymd, delta) {
  const base = fromZonedTime(`${ymd}T12:00:00`, CLINIC_TIMEZONE);
  const next = addDays(base, delta);
  return formatInTimeZone(next, CLINIC_TIMEZONE, 'yyyy-MM-dd');
}

export function addCalendarMonthsYmd(ymd, deltaMonths) {
  const base = fromZonedTime(`${ymd}T12:00:00`, CLINIC_TIMEZONE);
  const next = addMonths(base, deltaMonths);
  return formatInTimeZone(next, CLINIC_TIMEZONE, 'yyyy-MM-dd');
}

export function firstOfMonthYmd(ymd) {
  return `${ymd.slice(0, 7)}-01`;
}

/** Inicio UTC del día calendario CDMX; fin exclusivo (00:00 del día siguiente CDMX). */
export function clinicDayRangeUtc(ymd) {
  const start = fromZonedTime(`${ymd}T00:00:00`, CLINIC_TIMEZONE);
  const endExclusive = fromZonedTime(`${addCalendarDaysYmd(ymd, 1)}T00:00:00`, CLINIC_TIMEZONE);
  return { start, endExclusive };
}

/** Lunes…domingo (ISO) en CDMX que contiene `ymd`. */
export function isoWeekBoundsYmd(ymd) {
  const noon = fromZonedTime(`${ymd}T12:00:00`, CLINIC_TIMEZONE);
  const isoD = parseInt(formatInTimeZone(noon, CLINIC_TIMEZONE, 'i'), 10);
  const monNoon = addDays(noon, -(isoD - 1));
  const weekStartYmd = formatInTimeZone(monNoon, CLINIC_TIMEZONE, 'yyyy-MM-dd');
  const sunNoon = addDays(monNoon, 6);
  const weekEndYmd = formatInTimeZone(sunNoon, CLINIC_TIMEZONE, 'yyyy-MM-dd');
  return { weekStartYmd, weekEndYmd };
}

/** Primera y última celda (lunes–domingo) del grid de mes CDMX que cubre el mes de `ymd`. */
export function monthGridBoundsYmd(ymd) {
  const firstOfMonth = firstOfMonthYmd(ymd);
  const firstNoon = fromZonedTime(`${firstOfMonth}T12:00:00`, CLINIC_TIMEZONE);
  const isoFirst = parseInt(formatInTimeZone(firstNoon, CLINIC_TIMEZONE, 'i'), 10);
  const gridStartNoon = addDays(firstNoon, -(isoFirst - 1));
  const gridStartYmd = formatInTimeZone(gridStartNoon, CLINIC_TIMEZONE, 'yyyy-MM-dd');
  const lastOfMonthNoon = addDays(addMonths(firstNoon, 1), -1);
  const isoLast = parseInt(formatInTimeZone(lastOfMonthNoon, CLINIC_TIMEZONE, 'i'), 10);
  const gridEndNoon = addDays(lastOfMonthNoon, 7 - isoLast);
  const gridEndYmd = formatInTimeZone(gridEndNoon, CLINIC_TIMEZONE, 'yyyy-MM-dd');
  return { gridStartYmd, gridEndYmd };
}

export function eachYmdInclusive(startYmd, endYmd) {
  const out = [];
  let cur = startYmd;
  while (cur <= endYmd) {
    out.push(cur);
    cur = addCalendarDaysYmd(cur, 1);
  }
  return out;
}

/** Etiqueta de un día calendario CDMX (mediodía ancla evita bordes DST). */
export function formatYmdClinic(ymd, fmt, options = {}) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const d = fromZonedTime(`${ymd}T12:00:00`, CLINIC_TIMEZONE);
  return formatInTimeZone(d, CLINIC_TIMEZONE, fmt, { locale: defaultLocale, ...options });
}

/** Para react-day-picker u otros que esperan `Date`. */
export function ymdToDatePickerDate(ymd) {
  return fromZonedTime(`${ymd}T12:00:00`, CLINIC_TIMEZONE);
}

/** Día ISO de la semana (1=lunes … 7=domingo) en CDMX para ese día calendario. */
export function calendarIsoWeekdayFromYmd(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return NaN;
  return parseInt(formatInTimeZone(fromZonedTime(`${ymd}T12:00:00`, CLINIC_TIMEZONE), CLINIC_TIMEZONE, 'i'), 10);
}

/**
 * Rango UTC [start, end) para GET /admin/calendar-events según vista y día focal en CDMX.
 */
export function getCalendarApiRangeUtc(focusYmd, calendarView) {
  if (calendarView === 'day') {
    const { start, endExclusive } = clinicDayRangeUtc(focusYmd);
    return { start, end: endExclusive };
  }
  if (calendarView === 'week') {
    const { weekStartYmd, weekEndYmd } = isoWeekBoundsYmd(focusYmd);
    const start = fromZonedTime(`${weekStartYmd}T00:00:00`, CLINIC_TIMEZONE);
    const endExclusive = addDays(fromZonedTime(`${weekEndYmd}T00:00:00`, CLINIC_TIMEZONE), 1);
    return { start, end: endExclusive };
  }
  const { gridStartYmd, gridEndYmd } = monthGridBoundsYmd(focusYmd);
  const start = fromZonedTime(`${gridStartYmd}T00:00:00`, CLINIC_TIMEZONE);
  const endExclusive = addDays(fromZonedTime(`${gridEndYmd}T00:00:00`, CLINIC_TIMEZONE), 1);
  return { start, end: endExclusive };
}
